// The player: the reply's speech, pcm_s16le 24 kHz mono off the
// session socket, scheduled back to back so there is no gap between
// sentences. Ported from the orb's player.ts (the ticket's word: it
// moves over) onto the AudioSink port, so the scheduling is testable
// without a device; a chunk of another turn replaces what plays.
import { pcm16ToFloat } from "@/audio/pcm";
import type { AudioSink } from "@/audio/ports";

export const REPLY_RATE = 24000;

// A lead on the first chunk so a slow schedule does not click (the
// orb's word: a frame of rendering is enough to miss a 20 ms window).
const LEAD = 0.12;

export function createPlayer(sink: AudioSink, onDone?: (turn: string) => void) {
  // null: nothing has played yet — a first chunk flushes nothing (the
  // orb player's gen = −1).
  let turn: string | null = null;
  let odd: Uint8Array = new Uint8Array(0);
  let nextAt = 0;
  let ending = false;
  let pending = 0;

  function finish(t: string): void {
    if (pending === 0 && ending && t === turn) {
      ending = false;
      onDone?.(t);
    }
  }

  return {
    // One chunk of the turn's reply audio, in order.
    push(t: string, pcm: Uint8Array): void {
      if (t !== turn) {
        if (turn !== null) this.stop();
        turn = t;
        odd = new Uint8Array(0);
      }
      sink.resume();
      const { floats, odd: left } = pcm16ToFloat(pcm, odd);
      odd = left;
      if (floats.length === 0) return;
      const at = Math.max(sink.currentTime + LEAD, nextAt);
      nextAt = at + sink.play(at, floats, () => {
        pending -= 1;
        finish(t);
      });
      pending += 1;
    },
    // Nothing more is coming for this turn: report when drained.
    end(t: string): void {
      if (t !== turn) {
        if (turn !== null) onDone?.(t);
        return;
      }
      ending = true;
      finish(t);
    },
    // Barge-in or a new turn: flush everything now.
    stop(): void {
      sink.stopAll();
      pending = 0;
      ending = false;
      nextAt = 0;
    },
  };
}
